/* Shared lookbook (read-only client view) + print/PDF document.
   Ported from the Claude design's portal-share.jsx. */
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Ico } from './Icons';
import { ProductImage } from './ProductPeek';
import { Present } from './Present';
import type { PresentMeta } from './Present';
import { useAuth } from '@/portal/auth/AuthContext';
import { todayLong } from '@/portal/data/time';
import { orderByCategory } from '@/portal/data/product';
import type { PortalProduct, PortfolioDisplay } from '@/portal/api/types';

const LOGO = '/assets/didiers-logo.svg';

export interface LookbookContact {
  email: string;
  phone: string;
  phoneHref: string;
}

function ProductPlate({ p, display }: { p: PortalProduct; display: PortfolioDisplay }) {
  const d = display || {};
  const a = p.allergens || [];
  return (
    <div className="lb-plate">
      <div className="lb-plate-img">
        <ProductImage src={p.imgHi || p.img} alt={p.name} />
      </div>
      <div className="lb-plate-body">
        {d.category !== false && <div className="lb-range">{p.rangeLabel}</div>}
        <h3>{p.name}</h3>
        <div className="lb-code">{p.code}</div>
        {p.subtitle && <p className="lb-sub">{p.subtitle}</p>}
        {p.desc && <p className="lb-desc">{p.desc}</p>}
        <div className="lb-allergen">
          {a.length ? (
            <span>
              <b>Contains:</b> {a.join(', ')}
            </span>
          ) : (
            <span>No declared allergens</span>
          )}
        </div>
        {(d.packSize || d.weight || d.ingredients) && (
          <div className="lb-specs">
            {d.packSize && p.packSize && (
              <div className="row2">
                <span className="k">Case size</span>
                <span className="v">{p.packSize}</span>
              </div>
            )}
            {d.weight && p.weight && (
              <div className="row2">
                <span className="k">Weight</span>
                <span className="v">{p.weight}</span>
              </div>
            )}
            {d.ingredients && p.ingredients && (
              <div className="row2">
                <span className="k">Ingredients</span>
                <span className="v" dangerouslySetInnerHTML={{ __html: p.ingredients }} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function SharedLookbook({
  meta,
  products,
  display,
  contact,
  onClose,
  onPrint,
}: {
  meta: PresentMeta;
  products: PortalProduct[];
  display: PortfolioDisplay;
  contact: LookbookContact;
  onClose?: () => void;
  onPrint: () => void;
}) {
  const [presenting, setPresenting] = useState(false);
  const ordered = useMemo(() => orderByCategory(products), [products]);
  const enquireHref = `mailto:${contact.email}?subject=${encodeURIComponent(`Enquiry — ${meta.title}`)}`;

  return (
    <div className="lb">
      <div className="lb-top">
        <img src={LOGO} alt="Didier's Patisserie" />
        <div className="lb-top-actions">
          <button
            className="pl-btn"
            style={{ background: 'transparent', color: '#fff', borderColor: 'rgba(255,255,255,0.25)' }}
            onClick={() => setPresenting(true)}
            disabled={!products.length}
          >
            <Ico.play width={15} height={15} /> Present
          </button>
          <button
            className="pl-btn"
            style={{ background: 'transparent', color: '#fff', borderColor: 'rgba(255,255,255,0.25)' }}
            onClick={onPrint}
          >
            <Ico.download width={15} height={15} /> Download PDF
          </button>
          <a className="pl-btn primary" href={enquireHref} style={{ textDecoration: 'none' }}>
            <Ico.mail width={15} height={15} /> Enquire
          </a>
          {onClose && (
            <button
              className="pl-btn"
              style={{ background: 'transparent', color: '#fff', borderColor: 'rgba(255,255,255,0.25)' }}
              onClick={onClose}
            >
              <Ico.x width={15} height={15} /> Close preview
            </button>
          )}
        </div>
      </div>

      <div className="lb-hero">
        {meta.client && <div className="ey">{meta.client}</div>}
        <h1>{meta.title}</h1>
        {meta.event && <div className="event">{meta.event}</div>}
        {meta.note && <p className="note">“{meta.note}”</p>}
        <div className="meta">
          {products.length} selections · Prepared {todayLong()}
        </div>
      </div>
      <div className="lb-divider">
        <div className="rule" />
        <div className="tri" />
        <div className="rule" />
      </div>

      <div className="lb-list">
        {ordered.map((p) => (
          <ProductPlate key={p.id} p={p} display={display} />
        ))}
        {products.length === 0 && <div className="pl-empty">This selection is empty.</div>}
      </div>

      <div className="lb-foot">
        <div className="tag">London's finest patisserie made to royal standards</div>
        <div>
          To discuss this selection — <a href={`mailto:${contact.email}`}>{contact.email}</a> ·{' '}
          <a href={contact.phoneHref}>{contact.phone}</a>
        </div>
      </div>

      {presenting && (
        <Present meta={meta} products={products} display={display} onClose={() => setPresenting(false)} />
      )}
    </div>
  );
}

export function PrintLookbook({
  meta,
  products,
  display,
  contact,
  onClose,
}: {
  meta: PresentMeta;
  products: PortalProduct[];
  display: PortfolioDisplay;
  contact: LookbookContact;
  onClose: () => void;
}) {
  const ordered = useMemo(() => orderByCategory(products), [products]);
  const { user } = useAuth();
  const company = user?.company_name || user?.name || user?.username || '';
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  return createPortal(
    <div className="print-root" style={{ position: 'fixed', inset: 0, background: 'var(--cream)', zIndex: 320, overflowY: 'auto' }}>
      <div
        className="no-print"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          background: 'var(--slate)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 28px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Ico.download width={18} height={18} />
          <span style={{ fontSize: 15 }}>PDF preview — “{meta.title}”</span>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            className="pl-btn"
            style={{ background: 'transparent', color: '#fff', borderColor: 'rgba(255,255,255,0.25)' }}
            onClick={onClose}
          >
            Close
          </button>
          <button className="pl-btn primary" onClick={() => window.print()}>
            <Ico.download width={15} height={15} /> Save as PDF
          </button>
        </div>
      </div>
      <div
        className="no-print"
        style={{ textAlign: 'center', color: 'var(--ink-faint)', fontSize: 13, padding: '14px 20px 0' }}
      >
        Use “Save as PDF” (or your browser's print dialog → Save as PDF). A4, one cover page then product sheets.
      </div>

      <div
        className="print-doc"
        style={{ maxWidth: 820, margin: '20px auto 60px', background: '#fff', boxShadow: '0 8px 40px rgba(54,61,76,0.12)' }}
      >
        <div className="print-cover">
          <div className="band">
            {company && <div className="pl-cover-company">{company}</div>}
            <div className="pl-cover-collab">in collaboration with</div>
            <img src={LOGO} alt="Didier's Patisserie" />
          </div>
          <div style={{ padding: '56px 48px' }}>
            {meta.client && (
              <div style={{ color: 'var(--purple)', letterSpacing: '0.22em', textTransform: 'uppercase', fontSize: 13 }}>
                {meta.client}
              </div>
            )}
            <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 300, color: 'var(--ink)', fontSize: 46, margin: '16px 0 8px' }}>
              {meta.title}
            </h1>
            {meta.event && <div style={{ fontSize: 18, color: 'var(--ink-soft)' }}>{meta.event}</div>}
            {meta.note && (
              <p style={{ fontStyle: 'italic', fontFamily: 'var(--font-serif)', fontSize: 17, color: 'var(--ink-soft)', maxWidth: 560, margin: '24px auto 0' }}>
                “{meta.note}”
              </p>
            )}
            <div style={{ color: 'var(--ink-faint)', fontSize: 12.5, letterSpacing: '0.06em', textTransform: 'uppercase', marginTop: 30 }}>
              {products.length} selections · {todayLong()}
            </div>
          </div>
        </div>
        <div style={{ padding: '40px 48px' }}>
          <div className="lb-list" style={{ padding: 0, gap: 30, maxWidth: '100%' }}>
            {ordered.map((p) => (
              <ProductPlate key={p.id} p={p} display={display} />
            ))}
          </div>
          <div style={{ marginTop: 40, paddingTop: 24, borderTop: '1px solid var(--line)', textAlign: 'center', color: 'var(--ink-faint)', fontSize: 12.5 }}>
            Didier's Patisserie Ltd · {contact.email} · {contact.phone} · London's finest patisserie made to royal standards
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
